// Word (.docx) raporu — yatay (landscape) A4, iki sayfa, siyah-beyaz.
// Metin bölümleri NATIVE docx (Türkçe karakterler sorunsuz, seçilebilir);
// timeline grafiği B&W PNG olarak ImageRun ile gömülür.
// 1. sayfa = özet bilgiler; 2. sayfa = timeline grafiği (tam sayfaya sığar).

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  TableLayoutType,
  WidthType,
  BorderStyle,
  AlignmentType,
  VerticalAlign,
  ImageRun,
  PageOrientation,
  Footer,
  SimpleField,
} from 'docx';
import { saveAs } from 'file-saver';
import { documentStyles } from '../../components/wordComponents/documentStyles';
import { BORDER_COLOR } from '../../components/wordComponents/constants';
import { buildRaporVerisi, raporDosyaAdi, RAPOR_FERAGAT } from './reportData';
import { captureTimeline } from './timelineCapture';

// Raporun her sayfasına gelen footer: hukuki feragat + sayfa numarası.
function raporFooter() {
  return new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 40, after: 20 },
        border: { top: { style: BorderStyle.SINGLE, size: 4, color: 'CFD4DA', space: 4 } },
        children: [new TextRun({ text: RAPOR_FERAGAT, italics: true, size: 13, color: '6B7280' })],
      }),
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({ text: 'Sayfa ', size: 14, color: '9AA0A6' }),
          new SimpleField('PAGE'),
          new TextRun({ text: '/', size: 14, color: '9AA0A6' }),
          new SimpleField('NUMPAGES'),
        ],
      }),
    ],
  });
}

const NONE = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const THIN = { style: BorderStyle.SINGLE, size: 1, color: BORDER_COLOR };
const noBorders = { top: NONE, bottom: NONE, left: NONE, right: NONE, insideHorizontal: NONE, insideVertical: NONE };
const gridBorders = { top: THIN, bottom: THIN, left: THIN, right: THIN, insideHorizontal: THIN, insideVertical: THIN };

// Landscape A4 (16838 twip) − sol/sağ kenar (720 + 720) = kullanılabilir içerik genişliği.
// Apple/Mac yerleşik görüntüleyiciler tablo sütunlarını MS Word gibi OTOMATİK
// boyutlandırmaz; bu yüzden tablolara SABİT (DXA/twip) genişlik + FIXED yerleşim
// veriyoruz. Aksi halde sütunlar minimuma çöküp metin dikey diziliyordu.
const CONTENT_TWIPS = 16838 - 720 - 720; // 15398
const COL4 = Math.floor(CONTENT_TWIPS / 4); // 4 eşit sütun (künye/süre)
const COL4_WIDTHS = [COL4, COL4, COL4, CONTENT_TWIPS - COL4 * 3];
const BASLIK_SOL = Math.round(CONTENT_TWIPS * 0.68);
const BASLIK_SAG = CONTENT_TWIPS - BASLIK_SOL;

const spacer = (h = 120) => new Paragraph({ spacing: { after: h }, children: [] });

function kunyeCell(item, idx = 0) {
  return new TableCell({
    width: { size: COL4_WIDTHS[idx % 4], type: WidthType.DXA },
    margins: { top: 60, bottom: 60, left: 120, right: 120 },
    children: [
      new Paragraph({ children: [new TextRun({ text: item.etiket.toLocaleUpperCase('tr-TR'), size: 13, color: '6B7280' })] }),
      new Paragraph({ children: [new TextRun({ text: item.deger, size: 22, bold: true })] }),
    ],
  });
}

function sureCellKids(item) {
  const kids = [
    new Paragraph({ children: [new TextRun({ text: item.etiket.toLocaleUpperCase('tr-TR'), size: 13, color: '6B7280' })] }),
    new Paragraph({ children: [new TextRun({ text: item.deger, size: 26, bold: true })] }),
  ];
  if (item.alt) kids.push(new Paragraph({ children: [new TextRun({ text: item.alt, size: 13, color: '8A909B' })] }));
  return kids;
}

function baslikTablosu(rd) {
  return new Table({
    width: { size: CONTENT_TWIPS, type: WidthType.DXA },
    layout: TableLayoutType.FIXED,
    columnWidths: [BASLIK_SOL, BASLIK_SAG],
    borders: noBorders,
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: BASLIK_SOL, type: WidthType.DXA },
            borders: noBorders,
            verticalAlign: VerticalAlign.CENTER,
            children: [
              new Paragraph({ children: [new TextRun({ text: 'Dava Zamanaşımı Raporu', bold: true, size: 36 })] }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'TCK 66–67 uyarınca dava zamanaşımı değerlendirmesi',
                    size: 18,
                    color: '5B6470',
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: BASLIK_SAG, type: WidthType.DXA },
            borders: noBorders,
            verticalAlign: VerticalAlign.CENTER,
            shading: { fill: rd.doldu ? '14171C' : 'FFFFFF' },
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({ text: rd.durumMetni, bold: true, size: 24, color: rd.doldu ? 'FFFFFF' : '14171C' }),
                  ...(rd.durumDetay
                    ? [new TextRun({ text: '  (' + rd.durumDetay + ')', size: 16, color: rd.doldu ? 'D1D5DB' : '6B7280' })]
                    : []),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });
}

// Aşama aşama geçen süre: 3 sütunlu tablo (Aşama · Geçen süre · Toplam gün).
function asamaTablosu(rd) {
  const C1 = Math.round(CONTENT_TWIPS * 0.6);
  const C2 = Math.round(CONTENT_TWIPS * 0.22);
  const C3 = CONTENT_TWIPS - C1 - C2;
  const cellM = { top: 50, bottom: 50, left: 100, right: 100 };
  const head = (txt, w, align) =>
    new TableCell({
      width: { size: w, type: WidthType.DXA },
      margins: cellM,
      shading: { fill: 'F1F3F5' },
      children: [
        new Paragraph({
          alignment: align,
          children: [new TextRun({ text: txt.toLocaleUpperCase('tr-TR'), size: 13, bold: true, color: '5B626E' })],
        }),
      ],
    });

  const rows = [
    new TableRow({
      tableHeader: true,
      children: [
        head('Aşama', C1, AlignmentType.LEFT),
        head('Geçen süre', C2, AlignmentType.RIGHT),
        head('Toplam gün', C3, AlignmentType.RIGHT),
      ],
    }),
    ...rd.asamalar.map((a) => {
      const shading = a.accent ? { fill: 'F1F3F5' } : undefined;
      return new TableRow({
        children: [
          new TableCell({
            width: { size: C1, type: WidthType.DXA },
            margins: cellM,
            shading,
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: a.from + ' → ', size: 18, color: '5B6470' }),
                  new TextRun({ text: a.to, size: 18, bold: !!a.accent }),
                  ...(a.ref ? [new TextRun({ text: '  ' + a.ref, size: 14, color: '8A909B' })] : []),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: C2, type: WidthType.DXA },
            margins: cellM,
            shading,
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [new TextRun({ text: a.sure, size: 18, bold: !!a.accent })],
              }),
            ],
          }),
          new TableCell({
            width: { size: C3, type: WidthType.DXA },
            margins: cellM,
            shading,
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [new TextRun({ text: a.gun + ' gün', size: 18, color: '5B6470' })],
              }),
            ],
          }),
        ],
      });
    }),
  ];

  return new Table({
    width: { size: CONTENT_TWIPS, type: WidthType.DXA },
    layout: TableLayoutType.FIXED,
    columnWidths: [C1, C2, C3],
    borders: gridBorders,
    rows,
  });
}

async function dataUrlToUint8(dataUrl) {
  const res = await fetch(dataUrl);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
}

export async function indirWordRapor(sonuc, secimler) {
  const rd = buildRaporVerisi(sonuc, secimler);
  const tl = await captureTimeline(sonuc.timelineModel, sonuc);
  const tlData = await dataUrlToUint8(tl.dataUrl);

  // Landscape A4 içerik alanı ~@96dpi (twips/15): genişlik ~1026px, yükseklik ~718px.
  // Timeline 2. sayfada tek başına olduğundan başlık + açıklama payı düşülüp
  // grafik mümkün olan en büyük boyutta (tam sayfa) ölçeklenir.
  const CONTENT_PX = 1020;
  const MAX_TL_H = 600; // başlık (~30px) + açıklama (~60px) için pay bırakılır
  let drawW = CONTENT_PX;
  let drawH = Math.round(drawW / tl.aspect);
  if (drawH > MAX_TL_H) {
    drawH = MAX_TL_H;
    drawW = Math.round(drawH * tl.aspect);
  }

  // Künye: 8 öğe → 2 satır × 4. (idx → sabit sütun genişliği)
  const kunyeRows = [];
  for (let i = 0; i < rd.kunye.length; i += 4) {
    kunyeRows.push(new TableRow({ children: rd.kunye.slice(i, i + 4).map((item, j) => kunyeCell(item, j)) }));
  }
  const kunyeTablosu = new Table({
    width: { size: CONTENT_TWIPS, type: WidthType.DXA },
    layout: TableLayoutType.FIXED,
    columnWidths: COL4_WIDTHS,
    borders: gridBorders,
    rows: kunyeRows,
  });

  // Süre oluşumu: tek satır; öğe sayısı 4'ten azsa eşit sütunlarla genişliği korur.
  const sureCount = rd.sureOlusumu.length || 1;
  const sureCol = Math.floor(CONTENT_TWIPS / sureCount);
  const sureColWidths = rd.sureOlusumu.map((_, i) =>
    i === sureCount - 1 ? CONTENT_TWIPS - sureCol * (sureCount - 1) : sureCol
  );
  const sureTablosu = new Table({
    width: { size: CONTENT_TWIPS, type: WidthType.DXA },
    layout: TableLayoutType.FIXED,
    columnWidths: sureColWidths,
    borders: gridBorders,
    rows: [
      new TableRow({
        children: rd.sureOlusumu.map(
          (item, i) =>
            new TableCell({
              width: { size: sureColWidths[i], type: WidthType.DXA },
              margins: { top: 80, bottom: 80, left: 120, right: 120 },
              children: sureCellKids(item),
            })
        ),
      }),
    ],
  });

  const sec = rd.secimler || {};
  const children = [
    baslikTablosu(rd),
    spacer(140),
    new Paragraph({
      children: [
        new TextRun({ text: 'Geçerli zamanaşımı (' + rd.gecerliTur + '):  ', size: 18, color: '5B6470' }),
        new TextRun({ text: rd.effectiveUzun, bold: true, size: 30 }),
      ],
    }),
    new Paragraph({ spacing: { after: 160 }, children: [new TextRun({ text: rd.gerekce, italics: true, size: 18, color: '3B424C' })] }),
    kunyeTablosu,
  ];

  if (sec.sureOlusumu) {
    children.push(
      spacer(160),
      new Paragraph({ spacing: { after: 60 }, children: [new TextRun({ text: 'Sürenin oluşumu', bold: true, size: 24 })] }),
      sureTablosu
    );
  }

  if (sec.asamalar && rd.asamalar.length) {
    children.push(
      spacer(160),
      new Paragraph({ spacing: { after: 60 }, children: [new TextRun({ text: 'Aşama aşama geçen süre', bold: true, size: 24 })] }),
      asamaTablosu(rd)
    );
  }

  children.push(
    // --- 2. sayfa: zaman çizelgesi (başlık sayfa sonundan sonra başlar) ---
    new Paragraph({
      pageBreakBefore: true,
      spacing: { after: 60 },
      children: [new TextRun({ text: 'Zaman çizelgesi', bold: true, size: 24 })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new ImageRun({ data: tlData, transformation: { width: drawW, height: drawH } })],
    }),
    new Paragraph({
      spacing: { before: 120 },
      children: [
        new TextRun({ text: 'Açıklama: ', bold: true, size: 16, color: '14171C' }),
        new TextRun({
          text:
            'Olağan = koyu düz şerit · Olağanüstü (67/4) = gri kesik şerit · Durma = taralı bant · ' +
            'Kesme süreyi sıfırlar · Bugün = kesik dikey çizgi · Sonuç: dolmuş = taralı, kalan = boş.',
          size: 16,
          color: '3B424C',
        }),
      ],
    })
  );

  const doc = new Document({
    styles: documentStyles,
    sections: [
      {
        properties: {
          page: {
            // docx, orientation LANDSCAPE iken w/h değerlerini kendisi takas eder
            // (PageSize kurucusu). Bu yüzden ölçüler PORTRAIT (dikey) verilir:
            // width=11906 (210mm), height=16838 (297mm). Kütüphane bunları çevirip
            // w:w=16838 w:h=11906 w:orient=landscape üretir; ekran-yazıcı tutarlı olur.
            // Buraya landscape ölçülerini yazmak çift takasa ve dikey/yatay
            // tutarsızlığına yol açar.
            size: { orientation: PageOrientation.LANDSCAPE, width: 11906, height: 16838 },
            margin: { top: 567, bottom: 567, left: 720, right: 720 },
          },
        },
        children,
        footers: { default: raporFooter() },
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, raporDosyaAdi(sonuc, 'docx'));
}
